import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { IManageAssetTypeItem } from '@interfaces/platform/manageAssetTypes';

import { columnSizes } from '@constants/table';
import {
	addSubtypeTooltipText,
	deleteSubTypeTooltipText,
	deleteTypeTooltipText,
	editSubTypeTooltipText,
	editTypeTooltipText,
} from './constants';

import AssetCell from './AssetCell/AssetCell';

import actionsStyles from './ManageAssetTypes.module.scss';
import styles from '@components/common/Table/Table.module.scss';

interface IColumnProps {
	openAssetFormModal: () => void;
	openSubTypeModal: () => void;
	setSelectedRow: (row: any) => void;
	openDeleteModal: (isSubType: boolean) => void;
}

type TColumnAssetTypeProps = Omit<IManageAssetTypeItem, 'subTypes'> & { subRows: string };

export const getColumns = ({
	openAssetFormModal,
	openSubTypeModal,
	setSelectedRow,
	openDeleteModal,
}: IColumnProps) => ([
	{
		id: 'expander',
		size: columnSizes.size35,
		header: '',
		cell: ({ row }: CellContext<TColumnAssetTypeProps, any>) =>
			row.getCanExpand() ? (
				<span
					className={styles.expandAction}
					onClick={row.getToggleExpandedHandler()}
				>
					{row.getIsExpanded() ? Icons.chevronUp() : Icons.chevronDown()}
				</span>
			) : null,
	},
	{
		header: 'Asset Type',
		accessorKey: 'assetTypeName',
		size: columnSizes.size200,
		cell: ({ row: { original } }: CellContext<TColumnAssetTypeProps, any>) => {
			const { assetTypeName, icon } = original || {};

			return (
				<AssetCell
					assetName={assetTypeName}
					id={icon?.id}
				/>
			);
		},
	},
	{
		header: 'Asset Subtype',
		accessorKey: 'assetSubTypeName',
		size: columnSizes.size200,
		cell: ({ row: { original } }: CellContext<TColumnAssetTypeProps, any>) => {
			const text = original?.assetSubTypeName ? original?.assetSubTypeName : original.subRows?.length;

			return <AdaptiveTableCell text={text.toString()} />;
		},
	},
	{
		header: 'Used in Organisations',
		accessorKey: 'usedInOrganizations',
		cell: ({ getValue }: CellContext<TColumnAssetTypeProps, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'id',
		enableSorting: false,
		size: columnSizes.size115,
		cell: ({ row }: CellContext<TColumnAssetTypeProps, any>) => {
			const isSubType = row.depth !== 0;
			const data = row.original;
			const isUsedInOrganizations = data.usedInOrganizations > 0;

			const deleteTooltipText = isSubType ? deleteSubTypeTooltipText : deleteTypeTooltipText;
			const editTooltipText = isSubType ? editSubTypeTooltipText : editTypeTooltipText;

			const openHandler = () => {
				setSelectedRow(data);
				openSubTypeModal();
			};

			const editHandler = () => {
				setSelectedRow(data);

				if (isSubType) {
					openSubTypeModal();
				} else {
					openAssetFormModal();
				}
			};

			const deleteHandler = () => {
				setSelectedRow(data);
				openDeleteModal(isSubType);
			};

			return (
				<Actions
					addHandler={openHandler}
					addTooltip={addSubtypeTooltipText}
					containerClassName={isSubType ? actionsStyles.subTypeActionContainer : undefined}
					deleteTooltip={deleteTooltipText}
					editHandler={editHandler}
					editTooltip={editTooltipText}
					gridNumberOfColumns={3}
					handleDelete={deleteHandler}
					isAdd={!isSubType}
					isDelete={!isUsedInOrganizations}
					isEdit
					openModalHandler={openAssetFormModal}
				/>
			);
		},
	},
]);
